// src/hooks/useProactiveCentralRefresh.ts
// Proactive token refresh hook for Central authentication
// Schedules refresh 60 seconds before token expiry to reduce 401 errors

import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import {
  selectCentralAccessToken,
  selectCentralRefreshToken,
  selectCentralTokenExpiresAt,
  selectIsCentralAuthenticated,
  updateCentralAccessToken,
} from '../store/slices/centralAuthSlice';
import { selectIsBootReady } from '../store/slices/bootContextSlice';
import { proactiveRefreshCentralToken } from '../api/baseApi';

// Refresh 60 seconds before token expires
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000;

/**
 * Proactive token refresh hook for Central authentication.
 *
 * Features:
 * - Schedules refresh 60 seconds before token expiry
 * - Uses existing refresh mutex from baseApi.ts (single-flight behavior)
 * - Handles Page Visibility API to refresh stale tokens when tab becomes active
 * - Automatically reschedules on token change, logout, or workspace switch
 * - Falls back to existing 401-based refresh if proactive refresh fails
 *
 * This hook is designed to be additive and non-breaking:
 * - Does NOT replace the existing 401-based refresh mechanism
 * - Does NOT change any auth storage or API response shapes
 * - Only runs when Central authenticated
 *
 * Usage:
 * ```tsx
 * function App() {
 *   useProactiveCentralRefresh();
 *   return <AppRouter />;
 * }
 * ```
 */
export function useProactiveCentralRefresh(): void {
  const dispatch = useAppDispatch();

  // Central auth state
  const isCentralAuthenticated = useAppSelector(selectIsCentralAuthenticated);
  const accessToken = useAppSelector(selectCentralAccessToken);
  const refreshToken = useAppSelector(selectCentralRefreshToken);
  const expiresAt = useAppSelector(selectCentralTokenExpiresAt);
  const isBootReady = useAppSelector(selectIsBootReady);

  // Timer reference for scheduled refresh
  const refreshTimerRef = useRef<number | null>(null);

  // Visibility change handler reference
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  /**
   * Clear any scheduled refresh timer.
   */
  const clearRefreshTimer = (): void => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  /**
   * Perform the actual token refresh.
   * CRITICAL: Checks isBootReady to prevent refresh during workspace switch.
   */
  const performRefresh = async (): Promise<void> => {
    // CRITICAL: Do NOT refresh when boot is not ready (e.g. during workspace switch).
    // A refresh during switch could return a token for the OLD workspace.
    if (!isBootReady) {
      return;
    }

    // Double-check we still have a refresh token
    if (!refreshToken) {
      if (import.meta.env.DEV) {
        console.log('[useProactiveCentralRefresh] No refresh token available, skipping refresh');
      }
      return;
    }

    try {
      if (import.meta.env.DEV) {
        console.log('[useProactiveCentralRefresh] Starting proactive token refresh');
      }

      const result = await proactiveRefreshCentralToken(refreshToken);

      if (result) {
        // Success - update store with new tokens (including rotated refresh token if present)
        dispatch(
          updateCentralAccessToken({
            accessToken: result.accessToken,
            expiresIn: result.expiresIn,
            refreshToken: result.refreshToken,
          })
        );

        if (import.meta.env.DEV) {
          console.log('[useProactiveCentralRefresh] Token refreshed successfully', {
            newExpiresAt: new Date(Date.now() + result.expiresIn * 1000).toISOString(),
            hasRotatedRefreshToken: !!result.refreshToken,
          });
        }
      } else {
        // Refresh failed or another refresh was in progress
        // If another refresh was in progress, it will have updated the store
        // If refresh truly failed, the 401-based refresh will handle it as fallback
        if (import.meta.env.DEV) {
          console.log(
            '[useProactiveCentralRefresh] Proactive refresh returned null (either failed or another refresh was in progress)'
          );
        }
      }
    } catch (error) {
      // Error during refresh - log but don't logout
      // The 401-based refresh mechanism will handle this as fallback
      if (import.meta.env.DEV) {
        console.error('[useProactiveCentralRefresh] Error during proactive refresh:', error);
      }
    }
  };

  /**
   * Schedule a refresh to occur before token expiry.
   */
  const scheduleRefresh = (expiryTimestamp: number): void => {
    const now = Date.now();
    const timeUntilExpiry = expiryTimestamp - now;
    const timeUntilRefresh = timeUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;

    if (timeUntilRefresh <= 0) {
      // Token already expired or will expire within refresh window - refresh immediately
      if (import.meta.env.DEV) {
        console.log(
          '[useProactiveCentralRefresh] Token expires soon, refreshing immediately',
          {
            expiresAt: new Date(expiryTimestamp).toISOString(),
            timeUntilExpiry: Math.round(timeUntilExpiry / 1000) + 's',
          }
        );
      }
      performRefresh();
    } else {
      // Schedule refresh before expiry
      if (import.meta.env.DEV) {
        console.log('[useProactiveCentralRefresh] Scheduling proactive refresh', {
          expiresAt: new Date(expiryTimestamp).toISOString(),
          refreshIn: Math.round(timeUntilRefresh / 1000) + 's',
        });
      }

      clearRefreshTimer();
      refreshTimerRef.current = window.setTimeout(() => {
        performRefresh();
      }, timeUntilRefresh);
    }
  };

  /**
   * Handle page visibility change.
   * If the tab becomes visible and the token will expire soon, refresh immediately.
   */
  const handleVisibilityChange = (): void => {
    // CRITICAL: Do NOT refresh when boot is not ready (workspace switch in progress)
    if (!isBootReady) {
      return;
    }

    if (document.visibilityState === 'visible' && expiresAt) {
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // If token will expire within the refresh window, refresh now
      if (timeUntilExpiry <= REFRESH_BEFORE_EXPIRY_MS && timeUntilExpiry > 0) {
        if (import.meta.env.DEV) {
          console.log(
            '[useProactiveCentralRefresh] Tab became visible, token expires soon, refreshing',
            {
              expiresAt: new Date(expiresAt).toISOString(),
              timeUntilExpiry: Math.round(timeUntilExpiry / 1000) + 's',
            }
          );
        }
        performRefresh();
      }
    }
  };

  // Effect: Schedule proactive refresh based on token expiry
  useEffect(() => {
    // Only run if Central authenticated, boot is ready, and we have required data.
    // CRITICAL: Do NOT refresh when boot is not ready (e.g. during workspace switch).
    // A refresh during switch could return a token for the OLD workspace and clobber
    // the new workspace token, causing 401s on all subsequent CRM backend requests.
    if (!isCentralAuthenticated || !isBootReady || !accessToken || !refreshToken || !expiresAt) {
      clearRefreshTimer();
      return;
    }

    // Schedule the next refresh
    scheduleRefresh(expiresAt);

    // Cleanup: Clear timer on unmount or when dependencies change
    return () => {
      clearRefreshTimer();
    };
  }, [isCentralAuthenticated, isBootReady, accessToken, refreshToken, expiresAt, dispatch]);

  // Effect: Setup visibility change listener
  useEffect(() => {
    // Only setup listener if Central authenticated
    if (!isCentralAuthenticated) {
      return;
    }

    // Create and store handler
    visibilityHandlerRef.current = handleVisibilityChange;

    // Add listener
    document.addEventListener('visibilitychange', visibilityHandlerRef.current);

    // Cleanup: Remove listener on unmount
    return () => {
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
        visibilityHandlerRef.current = null;
      }
    };
  }, [isCentralAuthenticated, isBootReady, expiresAt, refreshToken, dispatch]);
}

export default useProactiveCentralRefresh;
